import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, readJson } from "../../lib/api";
import { useToast } from "../Toast";

// Manager feedback on one run: thumbs up/down and/or a note. The trimmer sees it on their home screen.
type Item = { id: number; author: string; rating: 1 | -1 | null; body: string | null; created_at: number };

export function RunFeedback({ runId }: { runId: number }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const key = ["run-feedback", runId];
  const { data } = useQuery({ queryKey: key, queryFn: () => api.get(`/api/runs/${runId}/feedback`).then((r) => readJson<Item[]>(r)) });
  const [rating, setRating] = useState<1 | -1 | null>(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  async function send() {
    setBusy(true);
    try {
      const res = await api.postJson(`/api/runs/${runId}/feedback`, { rating, body });
      if (!res.ok) { toast(((await res.json().catch(() => ({}))) as { error?: string }).error ?? "Couldn't send", "err"); return; }
      setRating(null); setBody("");
      await queryClient.invalidateQueries({ queryKey: key });
      toast("Feedback sent", "ok");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="run-actions edit-run-form" data-testid={`run-feedback-${runId}`}>
      {(data ?? []).map((f) => (
        <div key={f.id} className="small">
          {f.rating === 1 ? "👍 " : f.rating === -1 ? "👎 " : ""}{f.body}
          <span className="muted"> · {f.author}, {new Date(f.created_at * 1000).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</span>
        </div>
      ))}
      <div className="row" style={{ gap: 6, alignItems: "center" }}>
        <button type="button" className={`chip${rating === 1 ? " active" : ""}`} aria-pressed={rating === 1} aria-label="Thumbs up" onClick={() => setRating(rating === 1 ? null : 1)}>👍</button>
        <button type="button" className={`chip${rating === -1 ? " active" : ""}`} aria-pressed={rating === -1} aria-label="Thumbs down" onClick={() => setRating(rating === -1 ? null : -1)}>👎</button>
        <input value={body} maxLength={500} aria-label="Feedback note" placeholder="Note for the trimmer (optional)" onChange={(e) => setBody(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
        <button type="button" className="btn btn-primary btn-sm" disabled={busy || (rating === null && !body.trim())} data-testid={`send-feedback-${runId}`} onClick={() => void send()}>Send</button>
      </div>
    </div>
  );
}
